import { useNavigate, useLocation } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BASE_URL } from "../config";
import StaffAvatarCircle from "../components/StaffAvatarCircle";
import {
  IDENTIFICATION_ACTION,
  recordIdentificationAction,
} from "./identificationActionApi";
import { appendKioskShiftStatusParams } from "./manageContext";
import {
  formatAppIntlLabel,
  formatAppTime,
  getAppDisplayTimezone,
  parseDbDateTime,
} from "../utils/appDateTime";
import {
  getClientIdFromShiftLike,
  loadClientShiftRoundingSettings,
  normalizeShiftRoundingSettings,
  resolveShiftSettingsClientId,
  roundDateByShiftSettings,
} from "./shiftRounding";

const SUBMIT_MODES = {
  DEFAULT: "default",
  APPROVAL_REQUIRED: "approvalRequired",
  CONTACTS: "contacts",
  PASSCODE: "passcode",
  ASKING_FOR: "askingFor",
};

function formatDurationSeconds(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatTime(dateString) {
  return formatAppTime(dateString);
}

function formatDate(dateString) {
  if (!dateString) return "";
  return formatAppIntlLabel(dateString, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: getAppDisplayTimezone(),
  });
}

function parseShiftOptionDate(value) {
  if (!value) return null;
  const parsed = parseDbDateTime(value);
  if (parsed) return parsed;
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function toIsoIfValid(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toISOString();
}

function getLocationNameFromShiftLike(shiftLike) {
  return (
    shiftLike?.location ||
    shiftLike?.locationName ||
    shiftLike?.location_name ||
    shiftLike?.clientLocationName ||
    shiftLike?.client_location_name ||
    ""
  );
}

function normalizeScopeValue(value) {
  return String(value || "").trim().toLowerCase();
}

function nextShiftMatchesKioskScope(nextShift) {
  if (!nextShift) return false;
  const params = new URLSearchParams(window.location.search);
  const urlClientId = params.get("clientId");
  const urlLocation = params.get("location");
  const nextClientId = getClientIdFromShiftLike(nextShift);
  const nextLocation = getLocationNameFromShiftLike(nextShift);

  if (urlClientId && nextClientId && String(urlClientId) !== String(nextClientId)) {
    return false;
  }
  if (urlLocation && nextLocation) {
    return normalizeScopeValue(urlLocation) === normalizeScopeValue(nextLocation);
  }
  return true;
}

function buildShiftStatusState(data, previousUser) {
  const hasShift = Boolean(data?.hasShift || data?.hasScheduledShift);
  const nextShift = data?.nextShiftDetails || null;
  const shouldUseNextShift =
    !hasShift &&
    nextShiftMatchesKioskScope(nextShift) &&
    !data?.isCheckedIn;

  return {
    hasScheduledShift: hasShift || shouldUseNextShift,
    scheduledShiftDetails:
      data?.shiftDetails ||
      data?.scheduledShiftDetails ||
      (shouldUseNextShift ? nextShift : previousUser?.scheduledShiftDetails),
    isCheckedIn: shouldUseNextShift ? false : Boolean(data?.isCheckedIn),
    isCheckedOut: shouldUseNextShift ? false : Boolean(data?.isCheckedOut),
    checkinTime: shouldUseNextShift ? null : data?.checkinTime || previousUser?.checkinTime || null,
    nextShiftDetails: shouldUseNextShift ? null : nextShift || previousUser?.nextShiftDetails || null,
    checkedInShiftDetails:
      data?.checkedInShiftDetails || previousUser?.checkedInShiftDetails || null,
    checkedInShift:
      data?.checkedInShift || previousUser?.checkedInShift || null,
    currentShiftDetails:
      data?.currentShiftDetails || previousUser?.currentShiftDetails || null,
    currentShift:
      data?.currentShift || previousUser?.currentShift || null,
    activeShiftDetails:
      data?.activeShiftDetails || previousUser?.activeShiftDetails || null,
    activeShift:
      data?.activeShift || previousUser?.activeShift || null,
    hasAmbiguousShiftChoice: Boolean(
      data?.hasAmbiguousShiftChoice ?? previousUser?.hasAmbiguousShiftChoice,
    ),
    openShiftOption: data?.openShiftOption || previousUser?.openShiftOption || null,
    todayShiftOption: data?.todayShiftOption || previousUser?.todayShiftOption || null,
  };
}

function resolveCheckoutShiftDetails(user) {
  return (
    user?.checkedInShiftDetails ||
    user?.checkedInShift ||
    user?.currentShiftDetails ||
    user?.currentShift ||
    user?.activeShiftDetails ||
    user?.activeShift ||
    user?.scheduledShiftDetails ||
    {}
  );
}

function getShiftIdFromShiftLike(shiftLike) {
  return shiftLike?.shiftId || shiftLike?.shift_id || shiftLike?.id || null;
}

// Same field/source coverage as QRSuccess.js's resolveTimestampFromSources,
// since the API is inconsistent about which of these objects carries the
// actual check-in timestamp and under which key.
const CHECKIN_TIMESTAMP_FIELDS = [
  "checkinDate",
  "checkInDate",
  "checkin_date",
  "checkinTime",
  "checkin_time",
  "checkinAt",
  "check_in_at",
  "checkedInAt",
  "checked_in_at",
  "actualStartDate",
  "actual_start_date",
];

function resolveActualCheckInDate(user) {
  const sources = [
    user?.checkedInShiftDetails,
    user?.checkedInShift,
    user?.currentShiftDetails,
    user?.currentShift,
    user?.activeShiftDetails,
    user?.activeShift,
  ];
  for (const source of sources) {
    if (!source) continue;
    for (const field of CHECKIN_TIMESTAMP_FIELDS) {
      if (source[field]) return source[field];
    }
  }
  return null;
}

export default function ShiftDetails() {
  usePageMeta("Shift Details");
  const navigate = useNavigate();
  const location = useLocation();
  const { qrData, user: userFromState } = location.state || {};

  const [user, setUser] = useState(userFromState || null);
  const [policy, setPolicy] = useState(userFromState?.policy || null);
  const [checkinOptions, setCheckinOptions] = useState(userFromState?.checkinOptions || null);
  const [uiFlags, setUiFlags] = useState(userFromState?.ui || null);
  const [shiftRoundingSettings, setShiftRoundingSettings] = useState(
    userFromState?.shiftRoundingSettings || null,
  );
  const [statusLoading, setStatusLoading] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const [noteOptions, setNoteOptions] = useState([]);
  const [selectedNoteId, setSelectedNoteId] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);

  const [submitMode, setSubmitMode] = useState(SUBMIT_MODES.DEFAULT);
  const [extendedRequested, setExtendedRequested] = useState(false);
  const [extendedDurationAccountId, setExtendedDurationAccountId] = useState(null);
  const [extendedDurationPasscode, setExtendedDurationPasscode] = useState("");
  const [showPasscode, setShowPasscode] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [actualActionIso] = useState(() => {
    const fromState =
      userFromState?.actualActionIso ||
      userFromState?.identificationSubmittedAt ||
      userFromState?.identifiedAt;
    return toIsoIfValid(parseShiftOptionDate(fromState)) || new Date().toISOString();
  });
  const unscheduledRecordedRef = useRef(false);

  const hasAmbiguousShiftChoice = Boolean(user?.hasAmbiguousShiftChoice);
  const openShiftCandidate = user?.openShiftOption || null;
  const todayShiftCandidate = user?.todayShiftOption || null;
  const isAmbiguousMode = Boolean(
    hasAmbiguousShiftChoice &&
      openShiftCandidate?.shiftDetails &&
      todayShiftCandidate?.shiftDetails,
  );

  const [selectedShiftOptionKey, setSelectedShiftOptionKey] = useState(() => {
    if (userFromState?.hasAmbiguousShiftChoice) {
      return userFromState?.isCheckedIn ? "checkout" : "checkin";
    }
    return userFromState?.isCheckedIn ? "checkout" : "checkin";
  });

  const selectedShiftOptionKeyRef = useRef(selectedShiftOptionKey);
  useEffect(() => {
    selectedShiftOptionKeyRef.current = selectedShiftOptionKey;
  }, [selectedShiftOptionKey]);

  const isSelectedCheckout = isAmbiguousMode
    ? selectedShiftOptionKey === "checkout"
    : Boolean(user?.isCheckedIn && !user?.isCheckedOut);

  const hasShiftAtLocation = Boolean(
    isAmbiguousMode || user?.hasScheduledShift,
  );
  const isCheckedIn = isSelectedCheckout;
  const willCheckOut = isSelectedCheckout;
  const otherShift = isAmbiguousMode ? null : (user?.nextShiftDetails || null);

  const shiftDetails = useMemo(() => {
    const scheduledDetails = user?.scheduledShiftDetails || {};
    if (isAmbiguousMode) {
      return isSelectedCheckout
        ? openShiftCandidate?.shiftDetails || resolveCheckoutShiftDetails(user)
        : todayShiftCandidate?.shiftDetails || scheduledDetails;
    }
    return willCheckOut ? resolveCheckoutShiftDetails(user) : scheduledDetails;
  }, [
    isAmbiguousMode,
    isSelectedCheckout,
    openShiftCandidate,
    todayShiftCandidate,
    user,
    willCheckOut,
  ]);
  const scheduledStart = shiftDetails.startDate || shiftDetails.start_date;
  const scheduledEnd = shiftDetails.endDate || shiftDetails.end_date;

  const handleShiftOptionSwitch = useCallback(
    (key) => {
      if (key === selectedShiftOptionKey) return;
      setSelectedShiftOptionKey(key);
      setExtendedRequested(false);
      setSelectedNoteId("");
      setError("");

      const targetCandidate = key === "checkout" ? openShiftCandidate : todayShiftCandidate;
      if (targetCandidate) {
        if (targetCandidate.policy) setPolicy(targetCandidate.policy);
        if (targetCandidate.checkinOptions) setCheckinOptions(targetCandidate.checkinOptions);
        if (targetCandidate.ui) setUiFlags(targetCandidate.ui);
      }
    },
    [selectedShiftOptionKey, openShiftCandidate, todayShiftCandidate],
  );

  // Actual check-in timestamp for this shift, straight from
  // /api/face/check-shift-status's top-level `checkinTime`. Older responses
  // (or other entry flows) may not carry that field, so fall back to
  // scanning the shift-detail objects, and finally to backing it out of
  // checkinOptions.checkout (its durationMinutes is computed server-side
  // from the real check-in time, as (checkout date - checkout duration)).
  const actualCheckInDate = useMemo(() => {
    if (!willCheckOut) return null;
    const openShift = user?.openShiftOption?.shiftDetails;
    if (openShift) {
      for (const field of CHECKIN_TIMESTAMP_FIELDS) {
        if (openShift[field]) return openShift[field];
      }
    }
    if (user?.checkinTime) return user.checkinTime;
    const fromUser = resolveActualCheckInDate(user);
    if (fromUser) return fromUser;
    const base = checkinOptions?.checkout;
    if (base?.date && Number.isFinite(base?.durationMinutes)) {
      const baseDate = parseDbDateTime(base.date);
      if (baseDate) {
        return new Date(baseDate.getTime() - base.durationMinutes * 60000).toISOString();
      }
    }
    return null;
  }, [willCheckOut, user, checkinOptions]);

  const passcodeRequired = !!policy?.extendedDurationPasscodeRequired;
  const showEarlyOptions = !!uiFlags?.showEarlyCheckinOptions;
  const showLateOptions = !!uiFlags?.showLateCheckoutOptions;
  const showExtendedOptions = willCheckOut ? showLateOptions : showEarlyOptions;

  // "Original time" only applies when checking out at/after the scheduled end.
  // An early checkout must record the real scan time, not the scheduled end.
  const resolveOriginalOptionDate = useCallback(() => {
    if (!willCheckOut) return parseShiftOptionDate(scheduledStart);
    const scheduledEndDate = parseShiftOptionDate(scheduledEnd);
    const actualDate = parseShiftOptionDate(actualActionIso) || new Date();
    if (scheduledEndDate && actualDate < scheduledEndDate) return actualDate;
    return scheduledEndDate;
  }, [actualActionIso, scheduledEnd, scheduledStart, willCheckOut]);

  const getActualActionDate = useCallback(() => {
    const requestedDate = extendedRequested
      ? parseShiftOptionDate(
          willCheckOut
            ? checkinOptions?.lateCheckout?.date
            : checkinOptions?.earlyCheckin?.date,
        )
      : resolveOriginalOptionDate();
    return requestedDate || parseShiftOptionDate(actualActionIso) || new Date();
  }, [
    resolveOriginalOptionDate,
    actualActionIso,
    checkinOptions,
    extendedRequested,
    willCheckOut,
  ]);

  const getSelectedTimeOptionLog = useCallback(() => {
    const originalDate = resolveOriginalOptionDate();
    const askDate = parseShiftOptionDate(
      willCheckOut
        ? checkinOptions?.lateCheckout?.date
        : checkinOptions?.earlyCheckin?.date,
    );
    const actualDate = parseShiftOptionDate(actualActionIso);
    const selectedDate = extendedRequested
      ? askDate || actualDate
      : originalDate || actualDate;
    return {
      selectedOption: extendedRequested ? "ask" : "original",
      selectedOptionLabel: extendedRequested
        ? willCheckOut
          ? "Ask for shift to end"
          : "Ask for shift to start"
        : willCheckOut
          ? "Check me out for original end time"
          : "Check me in for original start time",
      selectedOptionTimeLabel: selectedDate ? formatTime(selectedDate) : "",
      selectedOptionIso: selectedDate ? selectedDate.toISOString() : null,
      originalShiftTimeLabel: originalDate ? formatTime(originalDate) : "",
      originalShiftIso: originalDate ? originalDate.toISOString() : null,
      askTimeLabel: askDate ? formatTime(askDate) : "",
      askIso: askDate ? askDate.toISOString() : null,
      actualFaceActionTimeLabel: actualDate ? formatTime(actualDate) : "",
      actualFaceActionIso: actualDate ? actualDate.toISOString() : null,
    };
  }, [
    resolveOriginalOptionDate,
    actualActionIso,
    checkinOptions,
    extendedRequested,
    willCheckOut,
  ]);

  useEffect(() => {
    if (!userFromState) navigate("/checkin" + window.location.search);
  }, [userFromState, navigate]);

  const refreshShiftStatus = useCallback(async () => {
    if (!userFromState?.accountId) return;
    setStatusLoading(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const qs = new URLSearchParams({ accountId: String(userFromState.accountId) });
      const timesheetId = urlParams.get("timesheetId") || qrData?.timesheetId;
      if (timesheetId) qs.set("timesheetId", String(timesheetId));
      const clientDeviceId = urlParams.get("clientDeviceId");
      if (clientDeviceId) qs.set("clientDeviceId", clientDeviceId);
      appendKioskShiftStatusParams(qs, window.location.search);

      const res = await fetch(`${BASE_URL}/api/face/check-shift-status?${qs.toString()}`);
      const data = await res.json();
      if (!data?.success) return;

      setUser((prev) => ({
        ...prev,
        ...buildShiftStatusState(data, prev),
        hasAmbiguousShiftChoice: Boolean(
          data?.hasAmbiguousShiftChoice ?? prev?.hasAmbiguousShiftChoice,
        ),
        openShiftOption: data?.openShiftOption || prev?.openShiftOption || null,
        todayShiftOption: data?.todayShiftOption || prev?.todayShiftOption || null,
      }));

      const isAmbiguous = Boolean(
        data?.hasAmbiguousShiftChoice && data?.openShiftOption && data?.todayShiftOption,
      );

      if (isAmbiguous) {
        let currentChoice = selectedShiftOptionKeyRef.current;
        if (currentChoice !== "checkout" && currentChoice !== "checkin") {
          currentChoice = data?.isCheckedIn ? "checkout" : "checkin";
          setSelectedShiftOptionKey(currentChoice);
        }
        const activeOpt =
          currentChoice === "checkout" ? data.openShiftOption : data.todayShiftOption;
        if (activeOpt?.policy) setPolicy(activeOpt.policy);
        if (activeOpt?.checkinOptions) setCheckinOptions(activeOpt.checkinOptions);
        if (activeOpt?.ui) setUiFlags(activeOpt.ui);
      } else {
        setPolicy(data.policy || null);
        setCheckinOptions(data.checkinOptions || null);
        setUiFlags(data.ui || null);
      }
      if (data.shiftSettings) {
        setShiftRoundingSettings(normalizeShiftRoundingSettings(data.shiftSettings));
      }
    } catch (e) {
      console.error("Failed to refresh shift status", e);
    } finally {
      setStatusLoading(false);
    }
  }, [userFromState?.accountId, qrData?.timesheetId]);

  useEffect(() => {
    refreshShiftStatus();
  }, [refreshShiftStatus]);

  useEffect(() => {
    let cancelled = false;
    const loadShiftSettings = async () => {
      if (shiftRoundingSettings) return;
      const clientId = resolveShiftSettingsClientId({
        user,
        shiftDetails,
        qrData,
        urlClientId: new URLSearchParams(window.location.search).get("clientId"),
      });
      if (!clientId) return;
      const settings = await loadClientShiftRoundingSettings(clientId);
      if (!cancelled && settings) {
        setShiftRoundingSettings(settings);
      }
    };
    loadShiftSettings();
    return () => {
      cancelled = true;
    };
  }, [shiftRoundingSettings, shiftDetails, user, qrData]);

  useEffect(() => {
    if (statusLoading || hasShiftAtLocation || !user?.identificationId) return;
    if (unscheduledRecordedRef.current) return;

    unscheduledRecordedRef.current = true;
    recordIdentificationAction(user.identificationId, {
      action: IDENTIFICATION_ACTION.UNSCHEDULED,
    }).catch((err) => {
      console.error("Failed to set identification action to unscheduled:", err);
      unscheduledRecordedRef.current = false;
    });
  }, [statusLoading, hasShiftAtLocation, user?.identificationId]);

  const noteScenarioType = useMemo(() => {
    if (!hasShiftAtLocation) return "";
    if (!willCheckOut) {
      return showEarlyOptions ? "checkInEarly" : "checkIn";
    }
    return showLateOptions ? "checkOutLate" : "checkOut";
  }, [hasShiftAtLocation, willCheckOut, showEarlyOptions, showLateOptions]);

  useEffect(() => {
    let mounted = true;
    const loadNoteOptions = async () => {
      if (!hasShiftAtLocation || !noteScenarioType) {
        setNoteOptions([]);
        setSelectedNoteId("");
        return;
      }
      setNoteLoading(true);
      try {
        const res = await fetch(
          `${BASE_URL}/api/face/checkin-notes?type=${encodeURIComponent(noteScenarioType)}`,
        );
        const payload = await res.json();
        if (!mounted) return;
        const rows = Array.isArray(payload?.data) ? payload.data : [];
        const options = rows
          .map((row) => ({
            id: String(row?.id || ""),
            label: String(row?.label || "").trim(),
          }))
          .filter((row) => row.id && row.label);
        setNoteOptions(options);
      } catch (fetchErr) {
        if (!mounted) return;
        setNoteOptions([]);
        console.error("Failed to load check-in notes:", fetchErr);
      } finally {
        if (mounted) setNoteLoading(false);
      }
    };

    loadNoteOptions();
    return () => {
      mounted = false;
    };
  }, [hasShiftAtLocation, noteScenarioType]);

  const loadContacts = async () => {
    setContactsLoading(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const qs = new URLSearchParams();
      const clientDeviceId = urlParams.get("clientDeviceId");
      const clientId =
        shiftDetails.clientId ||
        qrData?.clientDetails?.clientId ||
        urlParams.get("clientId");
      if (clientDeviceId) qs.set("clientDeviceId", clientDeviceId);
      if (clientId) qs.set("clientId", String(clientId));
      if (passcodeRequired) qs.set("passcodeRequired", "true");

      const res = await fetch(`${BASE_URL}/api/face/signin-contacts?${qs.toString()}`);
      const payload = await res.json();
      setContacts(Array.isArray(payload?.data) ? payload.data : []);
    } catch (e) {
      console.error("Failed to load sign-in contacts", e);
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const performCheckInOut = async () => {
    setIsProcessing(true);
    setError("");

    try {
      const endpoint = willCheckOut ? "/api/face/qr-checkout" : "/api/face/qr-checkin";
      const selectedNoteNumericId =
        selectedNoteId && Number.isFinite(Number(selectedNoteId))
          ? Number(selectedNoteId)
          : null;

      const urlParams = new URLSearchParams(window.location.search);
      const roundingClientId = resolveShiftSettingsClientId({
        user,
        shiftDetails,
        qrData,
        urlClientId: urlParams.get("clientId"),
      });

      let effectiveRoundingSettings = shiftRoundingSettings;
      if (!effectiveRoundingSettings) {
        effectiveRoundingSettings = await loadClientShiftRoundingSettings(roundingClientId);
        if (effectiveRoundingSettings) {
          setShiftRoundingSettings(effectiveRoundingSettings);
        }
      }

      const actualActionDateForSubmit = getActualActionDate();
      const roundingMode = willCheckOut
        ? effectiveRoundingSettings?.checkOutMode
        : effectiveRoundingSettings?.checkInMode;
      const actionDate = roundDateByShiftSettings(
        actualActionDateForSubmit,
        roundingMode,
        effectiveRoundingSettings,
        {
          field: willCheckOut ? "checkout" : "checkin",
          scheduledStart,
          scheduledEnd,
        },
      );
      const roundedActionIso = actionDate.toISOString();
      const actualActionIsoForSubmit = actualActionDateForSubmit.toISOString();
      const roundingChanged = actualActionIsoForSubmit !== roundedActionIso;
      const selectedTimeOptionLog = getSelectedTimeOptionLog();
      const extendedPayload = {
        ...(willCheckOut
          ? { lateCheckoutRequested: !!extendedRequested }
          : { earlyCheckinRequested: !!extendedRequested }),
        extendedDurationAccountId: extendedRequested ? extendedDurationAccountId : null,
        extendedDurationPasscode: extendedRequested ? extendedDurationPasscode : null,
        ...(extendedRequested && willCheckOut
          ? {
              lateCheckoutDate: roundedActionIso,
              late_checkout_date: roundedActionIso,
              requestedCheckoutDate: roundedActionIso,
              requested_checkout_date: roundedActionIso,
              extendedEndDate: roundedActionIso,
              extended_end_date: roundedActionIso,
            }
          : {}),
        ...(extendedRequested && !willCheckOut
          ? {
              earlyCheckinDate: roundedActionIso,
              early_checkin_date: roundedActionIso,
              requestedCheckinDate: roundedActionIso,
              requested_checkin_date: roundedActionIso,
              extendedStartDate: roundedActionIso,
              extended_start_date: roundedActionIso,
            }
          : {}),
      };
      const roundedTimePayload = willCheckOut
        ? {
            checkoutDate: roundedActionIso,
            checkOutDate: roundedActionIso,
            checkout_date: roundedActionIso,
            checkoutTime: roundedActionIso,
            checkout_time: roundedActionIso,
            checkoutAt: roundedActionIso,
            check_out_at: roundedActionIso,
            checkedOutAt: roundedActionIso,
            checked_out_at: roundedActionIso,
            actualEndDate: roundedActionIso,
            actual_end_date: roundedActionIso,
            actionTimestamp: roundedActionIso,
            actualActionTimestamp: actualActionIsoForSubmit,
            rounding: {
              enabled: !!effectiveRoundingSettings?.enabled,
              intervalMinutes: effectiveRoundingSettings?.intervalMinutes ?? null,
              mode: roundingMode || null,
            },
          }
        : {
            checkinDate: roundedActionIso,
            checkInDate: roundedActionIso,
            checkin_date: roundedActionIso,
            checkinTime: roundedActionIso,
            checkin_time: roundedActionIso,
            checkinAt: roundedActionIso,
            check_in_at: roundedActionIso,
            checkedInAt: roundedActionIso,
            checked_in_at: roundedActionIso,
            actualStartDate: roundedActionIso,
            actual_start_date: roundedActionIso,
            actionTimestamp: roundedActionIso,
            actualActionTimestamp: actualActionIsoForSubmit,
            rounding: {
              enabled: !!effectiveRoundingSettings?.enabled,
              intervalMinutes: effectiveRoundingSettings?.intervalMinutes ?? null,
              mode: roundingMode || null,
            },
          };

      const body = qrData
        ? {
            accountId: user.accountId,
            shiftId:
              getShiftIdFromShiftLike(shiftDetails) ||
              getShiftIdFromShiftLike(user.scheduledShiftDetails) ||
              qrData.shiftId,
            identificationId: user.identificationId || null,
            ...(willCheckOut ? {} : { timesheetId: qrData.timesheetId }),
            method: "qr-face",
            ...(willCheckOut
              ? { checkoutNoteId: selectedNoteNumericId }
              : { checkinNoteId: selectedNoteNumericId }),
            ...extendedPayload,
            ...roundedTimePayload,
          }
        : {
            accountId: user.accountId,
            shiftId:
              getShiftIdFromShiftLike(shiftDetails) ||
              getShiftIdFromShiftLike(user.scheduledShiftDetails),
            identificationId: user.identificationId || null,
            method: "face",
            ...(willCheckOut
              ? { checkoutNoteId: selectedNoteNumericId }
              : { checkinNoteId: selectedNoteNumericId }),
            ...extendedPayload,
            ...roundedTimePayload,
          };

      console.log(`[face-scan][rounding][${willCheckOut ? "checkout" : "checkin"}] before submit`, {
        action: willCheckOut ? "checkout" : "checkin",
        endpoint,
        clientId: roundingClientId || null,
        shiftId: body.shiftId,
        settingsLoadedBeforeSubmit: !!shiftRoundingSettings,
        settingsLoadedAtSubmit: !!effectiveRoundingSettings,
        settingsEnabled: !!effectiveRoundingSettings?.enabled,
        intervalMinutes: effectiveRoundingSettings?.intervalMinutes ?? null,
        mode: roundingMode || null,
        actualActionIso: actualActionIsoForSubmit,
        roundedActionIso,
        roundingChanged,
        selectedTimeOption: selectedTimeOptionLog,
        settings: effectiveRoundingSettings,
        roundedPayloadFields: roundedTimePayload,
      });
      console.log("[face-scan] submitting rounded action time", {
        action: willCheckOut ? "checkout" : "checkin",
        endpoint,
        clientId: roundingClientId || null,
        actualActionIso: actualActionIsoForSubmit,
        roundedActionIso,
        roundingChanged,
        selectedTimeOption: selectedTimeOptionLog,
        settings: effectiveRoundingSettings,
        mode: roundingMode || null,
        shiftId: body.shiftId,
      });

      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const result = await response.json();

      if (result.success) {
        const successRoute = qrData 
          ? `/checkin/qr-success${window.location.search}` 
          : `/checkin/success${window.location.search}`;
        navigate(successRoute, {
          state: {
            ...(qrData && { qrData }),
            user,
            successShiftDetails: shiftDetails,
            checkinData: result,
            isCheckout: willCheckOut,
            submittedActionTimestamp: roundedActionIso,
            actualActionTimestamp: actualActionIsoForSubmit,
            submittedTimestamp: roundedActionIso,
            submittedRoundedTimestamp: roundedActionIso,
            accountId: user.accountId,
            firstName: user.firstName,
            lastName: user.lastName,
            similarity: user.similarity,
          },
        });
      } else {
        setError(result.error || result.message || `${willCheckOut ? "Check-out" : "Check-in"} failed`);
        setIsProcessing(false);
      }
    } catch (err) {
      console.error(`${willCheckOut ? "Check-out" : "Check-in"} error:`, err);
      setError(`Failed to complete ${willCheckOut ? "check-out" : "check-in"}`);
      setIsProcessing(false);
    }
  };

  const verifyPasscode = async () => {
    if (!extendedDurationAccountId) {
      setError("Please select a contact");
      return false;
    }
    try {
      const res = await fetch(`${BASE_URL}/api/face/validate-extended-passcode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          extendedDurationAccountId,
          extendedDurationPasscode,
        }),
      });
      const data = await res.json();
      if (!data?.valid) {
        setError("Invalid passcode");
        return false;
      }
      return true;
    } catch (e) {
      setError("Invalid passcode");
      return false;
    }
  };

  const getNextSubmitMode = (fromMode) => {
    if (fromMode === SUBMIT_MODES.DEFAULT) {
      if (extendedRequested) {
        return passcodeRequired
          ? SUBMIT_MODES.APPROVAL_REQUIRED
          : SUBMIT_MODES.ASKING_FOR;
      }
      return SUBMIT_MODES.DEFAULT;
    }
    if (fromMode === SUBMIT_MODES.APPROVAL_REQUIRED) return SUBMIT_MODES.CONTACTS;
    if (fromMode === SUBMIT_MODES.CONTACTS) return SUBMIT_MODES.PASSCODE;
    if (fromMode === SUBMIT_MODES.PASSCODE) return SUBMIT_MODES.DEFAULT;
    if (fromMode === SUBMIT_MODES.ASKING_FOR) return SUBMIT_MODES.DEFAULT;
    return SUBMIT_MODES.DEFAULT;
  };

  const handleCancel = () => {
    if (submitMode !== SUBMIT_MODES.DEFAULT) {
      if (submitMode === SUBMIT_MODES.PASSCODE) {
        setSubmitMode(SUBMIT_MODES.CONTACTS);
        return;
      }
      if (submitMode === SUBMIT_MODES.CONTACTS) {
        setSubmitMode(SUBMIT_MODES.APPROVAL_REQUIRED);
        return;
      }
      if (submitMode === SUBMIT_MODES.APPROVAL_REQUIRED) {
        setSubmitMode(SUBMIT_MODES.DEFAULT);
        return;
      }
      if (submitMode === SUBMIT_MODES.ASKING_FOR) {
        setSubmitMode(SUBMIT_MODES.DEFAULT);
        return;
      }
    }
    navigate("/checkin" + window.location.search);
  };

  const handlePrimaryAction = async () => {
    setError("");

    if (submitMode === SUBMIT_MODES.PASSCODE) {
      const ok = await verifyPasscode();
      if (!ok) return;
      await performCheckInOut();
      return;
    }

    if (submitMode === SUBMIT_MODES.ASKING_FOR) {
      await performCheckInOut();
      return;
    }

    const next = getNextSubmitMode(submitMode);
    if (next !== SUBMIT_MODES.DEFAULT) {
      if (next === SUBMIT_MODES.CONTACTS) {
        await loadContacts();
      }
      setSubmitMode(next);
      return;
    }

    await performCheckInOut();
  };

  const handleContactSelect = (contact) => {
    setExtendedDurationAccountId(contact.id);
    setSubmitMode(SUBMIT_MODES.PASSCODE);
  };

  const handleExtendedOptionChange = (nextExtendedRequested) => {
    const originalDate = parseShiftOptionDate(willCheckOut ? scheduledEnd : scheduledStart);
    const askDate = parseShiftOptionDate(
      willCheckOut
        ? checkinOptions?.lateCheckout?.date
        : checkinOptions?.earlyCheckin?.date,
    );
    const selectedDate = nextExtendedRequested ? askDate : originalDate;
    console.log(`[face-scan][option][${willCheckOut ? "checkout" : "checkin"}] selected`, {
      selectedOption: nextExtendedRequested ? "ask" : "original",
      selectedOptionLabel: nextExtendedRequested
        ? willCheckOut
          ? "Ask for shift to end"
          : "Ask for shift to start"
        : willCheckOut
          ? "Check me out for original end time"
          : "Check me in for original start time",
      selectedOptionTimeLabel: selectedDate ? formatTime(selectedDate) : "",
      selectedOptionIso: selectedDate ? selectedDate.toISOString() : null,
      originalShiftTimeLabel: originalDate ? formatTime(originalDate) : "",
      originalShiftIso: originalDate ? originalDate.toISOString() : null,
      askTimeLabel: askDate ? formatTime(askDate) : "",
      askIso: askDate ? askDate.toISOString() : null,
    });
    setExtendedRequested(nextExtendedRequested);
  };

  const originalTimeLabel = willCheckOut
    ? formatTime(scheduledEnd)
    : formatTime(scheduledStart);

  const actualExtendedDate = useMemo(() => {
    const rawDate = willCheckOut
      ? checkinOptions?.lateCheckout?.date
      : checkinOptions?.earlyCheckin?.date;
    const parsed = parseShiftOptionDate(rawDate);
    return parsed ? parsed.toISOString() : rawDate;
  }, [checkinOptions, willCheckOut]);

  const actualActionDate = useMemo(
    () => getActualActionDate(),
    [getActualActionDate],
  );
  const actualActionTimeLabel = formatTime(actualActionDate);
  const extendedTimeLabel = formatTime(actualExtendedDate);

  const durationLabel = willCheckOut
    ? formatDurationSeconds(uiFlags?.lateCheckoutDurationSeconds)
    : formatDurationSeconds(uiFlags?.earlyCheckinDurationSeconds);

  const shiftTimeSummary = useMemo(() => {
    if (!scheduledStart) return "";
    // Once checked in, anchor the summary to the actual check-in time (not
    // the scheduled start) so the displayed range and total always agree.
    const startOpt = willCheckOut
      ? actualCheckInDate || scheduledStart
      : extendedRequested
        ? actualExtendedDate
        : scheduledStart;
    const endOpt =
      extendedRequested && willCheckOut
        ? actualExtendedDate
        : scheduledEnd;
    const start = formatTime(startOpt);
    const end = formatTime(endOpt);
    const startDt = parseDbDateTime(startOpt);
    const endDt = parseDbDateTime(endOpt);
    const totalSeconds =
      startDt && endDt
        ? Math.max(0, Math.floor((endDt.getTime() - startDt.getTime()) / 1000))
        : 0;
    const total = formatDurationSeconds(totalSeconds);
    return `Shift time: ${start} - ${end}, ${total} total`;
  }, [
    scheduledStart,
    scheduledEnd,
    extendedRequested,
    willCheckOut,
    actualExtendedDate,
    actualCheckInDate,
  ]);

  const primaryButtonLabel = () => {
    if (isProcessing) return willCheckOut ? "CHECKING OUT..." : "CHECKING IN...";
    if (submitMode === SUBMIT_MODES.APPROVAL_REQUIRED) return "SIGN IN & APPROVE";
    if (submitMode === SUBMIT_MODES.PASSCODE) return "APPROVE";
    if (submitMode === SUBMIT_MODES.ASKING_FOR) {
      return willCheckOut ? "ASK FOR SHIFT END" : "ASK FOR SHIFT START";
    }
    return willCheckOut ? "CHECK OUT" : "CHECK IN";
  };

  if (!user) return null;

  const pageTitle = () => {
    if (submitMode === SUBMIT_MODES.APPROVAL_REQUIRED) return "Manager approval required";
    if (submitMode === SUBMIT_MODES.ASKING_FOR) {
      return willCheckOut ? "Asking for shift end" : "Asking for shift start";
    }
    if (submitMode === SUBMIT_MODES.CONTACTS || submitMode === SUBMIT_MODES.PASSCODE) {
      return "Manager approval required";
    }
    return willCheckOut ? "Check Out" : "Check In";
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-3 overflow-y-auto">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 my-4">
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-gray-800 mb-2">{pageTitle()}</h1>
          {submitMode === SUBMIT_MODES.DEFAULT && (
            <>
              {qrData?.clientDetails ? (
                <>
                  <p className="text-sm text-gray-600">
                    {qrData.clientDetails.clientName || "Unknown Client"}
                  </p>
                  {user.scheduledShiftDetails?.location && (
                    <p className="text-sm text-gray-600">
                      Location: {user.scheduledShiftDetails.location}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-600">
                  {user.firstName} {user.lastName}
                </p>
              )}
            </>
          )}
        </div>

        {statusLoading && submitMode === SUBMIT_MODES.DEFAULT && (
          <p className="text-center text-sm text-gray-500 mb-4">Loading shift options...</p>
        )}

        {submitMode === SUBMIT_MODES.APPROVAL_REQUIRED && (
          <div className="mb-4 text-sm text-gray-700 text-center">
            <p className="mb-2">
              {user.firstName} {user.lastName} is {willCheckOut ? "checking out" : "checking in"}{" "}
              {formatDurationSeconds(uiFlags?.shiftStartTimeNowDiffSeconds)}{" "}
              {willCheckOut
                ? "from the scheduled shift end time."
                : "from the scheduled shift start time."}
            </p>
          </div>
        )}

        {submitMode === SUBMIT_MODES.CONTACTS && (
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-2 text-center">Sign-in</p>
            {contactsLoading ? (
              <p className="text-sm text-gray-500 text-center">Loading contacts...</p>
            ) : contacts.length === 0 ? (
              <p className="text-sm text-red-600 text-center">No approving contacts available</p>
            ) : (
              <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
                {contacts.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => handleContactSelect(c)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
                    >
                      <StaffAvatarCircle
                        accountId={c.id}
                        employee={{
                          account_id: c.id,
                          first_name: c.firstName,
                          last_name: c.lastName,
                          avatar_url: c.avatar,
                        }}
                        name={`${c.firstName || ""} ${c.lastName || ""}`.trim()}
                        className="w-10 h-10"
                        textClassName="text-sm font-semibold text-blue-800"
                      />
                      <span className="text-gray-800">
                        {c.firstName} {c.lastName}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {submitMode === SUBMIT_MODES.PASSCODE && (
          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-1">Client passcode</label>
            <div className="relative">
              <input
                type={showPasscode ? "text" : "password"}
                value={extendedDurationPasscode}
                onChange={(e) => setExtendedDurationPasscode(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10"
                placeholder="Client passcode"
                autoComplete="off"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500"
                onClick={() => setShowPasscode((v) => !v)}
              >
                {showPasscode ? "Hide" : "Show"}
              </button>
            </div>
          </div>
        )}

        {submitMode === SUBMIT_MODES.ASKING_FOR && (
          <p className="text-sm text-gray-600 text-center mb-4">
            Your request will be submitted for manager review using the extended{" "}
            {willCheckOut ? "end" : "start"} time.
          </p>
        )}

        {submitMode === SUBMIT_MODES.DEFAULT && isAmbiguousMode && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3.5 shadow-sm">
            <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <svg
                className="w-4 h-4 text-amber-600 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              Select Shift Action:
            </p>
            <div className="space-y-2">
              <label
                className={`flex items-start gap-3 p-2.5 rounded-md border cursor-pointer transition-colors ${
                  selectedShiftOptionKey === "checkout"
                    ? "bg-white border-amber-500 ring-1 ring-amber-500 shadow-sm"
                    : "bg-white/70 border-amber-200 hover:bg-white text-gray-700"
                }`}
              >
                <input
                  type="radio"
                  name="ambiguousShiftChoice"
                  value="checkout"
                  checked={selectedShiftOptionKey === "checkout"}
                  onChange={() => handleShiftOptionSwitch("checkout")}
                  className="mt-1 h-4 w-4 text-amber-600 border-gray-300 focus:ring-amber-500"
                />
                <div className="text-sm">
                  <div className="font-semibold text-gray-900">
                    Check out from previous shift
                  </div>
                  <div className="text-xs text-gray-600">
                    {formatDate(
                      openShiftCandidate?.shiftDetails?.startDate ||
                        openShiftCandidate?.shiftDetails?.start_date,
                    )}
                    :{" "}
                    {formatTime(
                      openShiftCandidate?.shiftDetails?.startDate ||
                        openShiftCandidate?.shiftDetails?.start_date,
                    )}{" "}
                    &ndash;{" "}
                    {formatTime(
                      openShiftCandidate?.shiftDetails?.endDate ||
                        openShiftCandidate?.shiftDetails?.end_date,
                    )}
                  </div>
                </div>
              </label>

              <label
                className={`flex items-start gap-3 p-2.5 rounded-md border cursor-pointer transition-colors ${
                  selectedShiftOptionKey === "checkin"
                    ? "bg-white border-amber-500 ring-1 ring-amber-500 shadow-sm"
                    : "bg-white/70 border-amber-200 hover:bg-white text-gray-700"
                }`}
              >
                <input
                  type="radio"
                  name="ambiguousShiftChoice"
                  value="checkin"
                  checked={selectedShiftOptionKey === "checkin"}
                  onChange={() => handleShiftOptionSwitch("checkin")}
                  className="mt-1 h-4 w-4 text-amber-600 border-gray-300 focus:ring-amber-500"
                />
                <div className="text-sm">
                  <div className="font-semibold text-gray-900">
                    Check in for today&apos;s shift
                  </div>
                  <div className="text-xs text-gray-600">
                    {formatDate(
                      todayShiftCandidate?.shiftDetails?.startDate ||
                        todayShiftCandidate?.shiftDetails?.start_date,
                    )}
                    :{" "}
                    {formatTime(
                      todayShiftCandidate?.shiftDetails?.startDate ||
                        todayShiftCandidate?.shiftDetails?.start_date,
                    )}{" "}
                    &ndash;{" "}
                    {formatTime(
                      todayShiftCandidate?.shiftDetails?.endDate ||
                        todayShiftCandidate?.shiftDetails?.end_date,
                    )}
                  </div>
                </div>
              </label>
            </div>
          </div>
        )}

        {submitMode === SUBMIT_MODES.DEFAULT && hasShiftAtLocation && (
          <>
            <div className="bg-green-50 p-4 rounded-lg mb-4 text-sm text-green-800">
              <div className="font-semibold mb-2">
                {shiftDetails.locationName || shiftDetails.location || "Shift"}
              </div> 
              <div>
                Time: {formatTime(scheduledStart)} - {formatTime(scheduledEnd)}
              </div>
              <div>Date: {formatDate(scheduledStart)}</div>
              <div>
                Actual {willCheckOut ? "check-out" : "check-in"} time: {actualActionTimeLabel}
              </div>
              <div className="mt-2 font-medium">
                Status: {isCheckedIn ? "Checked In" : "Not Checked In"}
              </div>
            </div>

            {willCheckOut && policy && !policy.allowExtendedDuration && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                Extended duration is not enabled for this client. Enable it under Staffing → Shift settings.
              </p>
            )}
            {willCheckOut &&
              policy?.allowExtendedDuration &&
              !showLateOptions &&
              shiftDetails.endDate && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                  Extended checkout and passcode options appear after scheduled end ({formatTime(shiftDetails.endDate)})
                  plus grace ({policy.graceMinutes ?? 0} min). Save shift times in admin before testing if you edited end time.
                </p>
              )}
            {showExtendedOptions && (
              <div className="mb-4 space-y-3">
                <p className="text-sm text-gray-700">
                  {willCheckOut
                    ? `You're checking out ${durationLabel} from your scheduled shift end time`
                    : `You're checking in ${durationLabel} from your scheduled shift start time. Please select one option below.`}
                </p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="extendedOption"
                    checked={!extendedRequested}
                    onChange={() => handleExtendedOptionChange(false)}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-800">
                    {willCheckOut
                      ? `Check me out for original end time (${originalTimeLabel})`
                      : `Check me in for original start time (${originalTimeLabel})`}
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="extendedOption"
                    checked={extendedRequested}
                    onChange={() => handleExtendedOptionChange(true)}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-800">
                    {willCheckOut
                      ? `Ask for shift to end (${extendedTimeLabel})`
                      : `Ask for shift to start (${extendedTimeLabel})`}
                  </span>
                </label>
              </div>
            )}

            {shiftTimeSummary && (
              <p className="text-sm text-gray-600 mb-4">{shiftTimeSummary}</p>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {isCheckedIn ? "Check out note" : "Check in note"}
              </label>
              <select
                value={selectedNoteId}
                onChange={(e) => setSelectedNoteId(e.target.value)}
                disabled={noteLoading || isProcessing}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
              >
                <option value="">None</option>
                {noteOptions.map((note) => (
                  <option key={note.id} value={note.id}>
                    {note.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {submitMode === SUBMIT_MODES.DEFAULT && !hasShiftAtLocation && (
          <div className="bg-red-50 p-4 rounded-lg mb-4 text-sm text-red-800">
            You don&apos;t have a shift today here
          </div>
        )}

        {otherShift && submitMode === SUBMIT_MODES.DEFAULT && (
          <div className="bg-yellow-50 p-4 rounded-lg mb-4 text-sm text-yellow-800">
            <div className="font-semibold mb-1">Shift at another location</div>
            <div>{otherShift.location}</div>
            <div>
              {formatTime(otherShift.startDate)} - {formatTime(otherShift.endDate)}
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3">
          {(hasShiftAtLocation || submitMode !== SUBMIT_MODES.DEFAULT) &&
            submitMode !== SUBMIT_MODES.CONTACTS && (
              <button
                type="button"
                onClick={handlePrimaryAction}
                disabled={isProcessing || (submitMode === SUBMIT_MODES.DEFAULT && statusLoading)}
                className="w-full bg-[#DC1F00] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#b91900] disabled:bg-gray-400"
              >
                {primaryButtonLabel()}
              </button>
            )}

          <button
            type="button"
            onClick={handleCancel}
            className="w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-semibold hover:bg-gray-300"
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
}
